
import {
  computed,
  defineComponent, onMounted, ref, Ref, watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import AppHeader from '@/components/AppHeader.vue';
import AppLoader from '@/components/UI/AppLoader.vue';
import DriveService from '@/modules/drive/services/DriveService';
import { IMixedData, IMixedSelectedItem } from '@/types/interfaces';
import NotificationService from '@/services/NotificationService';
import MultipleSelectionPopUp from './BottomPopUp/PopUpTypes/MultipleSelectionPopUp.vue';
import MoveToPopUp from './BottomPopUp/PopUpTypes/MoveTo/MoveToPopUp.vue';
import { MEDIA_TYPES, MIXED_DATA_TYPE } from '@/types/enums';
import SkeletonService from '@/services/SkeletonService';
import AppQuestionPopUp from '@/components/AppQuestionPopUp.vue';

export default defineComponent({
  emits: ['close'],
  components: {
    AppHeader,
    AppLoader,
    MultipleSelectionPopUp,
    MoveToPopUp,
    AppQuestionPopUp,
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const route = useRoute();

    const mixedData = computed<IMixedData[]>(() => {
      if (driveStore.search) {
        return driveStore.searchMixedData;
      }
      const folderId = driveStore.currentFolder?.id;
      return !folderId ? driveStore.mixedData.data : driveStore.currentFolder!.mixedData || [];
    });

    const bigLoader: Ref<string> = ref('');
    const isMixedSelected = computed(() => mixedData.value.filter((mix) => mix.selected).length);
    const isQuestionPopUp: Ref<boolean> = ref(false);

    const showMultiSelection = ref(false);
    const isMoveToPopUp = ref(false);

    const selectedMixed = computed(() => mixedData.value.filter((mix) => mix.selected));
    const selectedMixedIds = computed<IMixedSelectedItem[]>(
      () => selectedMixed.value.map((mixed) => ({
        id: mixed.id,
        type: mixed.type,
        folderId: mixed.file?.folderId || mixed.case?.folderId || mixed.folder?.upperFolderId || undefined,
      })),
    );

    function openMoveToPopUp() {
      showMultiSelection.value = false;
      isMoveToPopUp.value = true;
    }

    function openQuestionPopUp() {
      showMultiSelection.value = false;
      isQuestionPopUp.value = true;
    }

    function deleteMixed() {
      // bigLoader.value = 'Deleting files...';

      DriveService.deleteBatchMixed(selectedMixedIds.value).then(() => {
      //   driveStore.mixedData = mixedData.value.filter((mix) => !mix.selected);
      //   if (driveStore.currentFolder) {
      //     driveStore.currentFolder.mixedData = driveStore.mixedData;
      //   }
      //   emit('close');
      //   NotificationService.notifySuccess();
      });
      // .finally(() => { bigLoader.value = ''; });
      const selected = mixedData.value.filter((mix) => mix.selected);
      driveStore.cases.data = driveStore.cases.data.filter((iCase) => !selected.find((sel) => sel.id === iCase.id));
      driveStore.photos.data = driveStore.photos.data.filter((photo) => !selected.find((sel) => sel.id === photo.id));
      driveStore.searchMixedData = driveStore.searchMixedData.filter((mix) => !mix.selected);
      if (driveStore.currentFolder) {
        driveStore.currentFolder.mixedData = mixedData.value.filter((mix) => !mix.selected);
      } else {
        driveStore.mixedData.data = mixedData.value.filter((mix) => !mix.selected);
      }
      emit('close');
    }

    async function resolveMoveAction(item: IMixedSelectedItem, folderId: string | null) {
      if (item.type === MIXED_DATA_TYPE.CASE) {
        return DriveService.moveCase(item.id, folderId);
      }
      if (item.type === MIXED_DATA_TYPE.FOLDER) {
        return DriveService.moveFolder(item.id, folderId);
      }
      return DriveService.moveFile(item.id, folderId);
    }

    function clearCachedItem(itemId: string): void {
      if (driveStore.currentCase) {
        driveStore.currentCase.photos = driveStore.currentCase.photos.filter((photo) => photo.id !== itemId);
        driveStore.currentCase.files = driveStore.currentCase.files.filter((file) => file.id !== itemId);
      }
      if (driveStore.currentFolder?.mixedData) {
        driveStore.currentFolder.mixedData = driveStore.currentFolder.mixedData.filter((mix) => mix.id !== itemId);
      }
      driveStore.photos.data = driveStore.photos.data.filter((photo) => photo.id !== itemId);
      driveStore.files = driveStore.photos.data.filter((file) => file.id !== itemId);
      driveStore.mixedData.data = driveStore.mixedData.data.filter((mix) => mix.id !== itemId);
      driveStore.photoList = driveStore.photoList.filter((photo) => photo.id !== itemId);
    }

    function setSkeletons(folderId: string | null, amount: number) {
      if (folderId) {
        SkeletonService.setFolderSkeletons(folderId, amount, MEDIA_TYPES.FILES);
      }
    }

    function removeSkeletons(folderId: string | null, amount: number) {
      if (folderId) {
        SkeletonService.removeFolderSkeletons(folderId, amount, MEDIA_TYPES.FILES);
      }
    }

    function syncData(folderId: string | null = null) {
      if (folderId && driveStore.currentFolder?.id === folderId) {
        const hasItem = driveStore.currentFolder.mixedData?.find((mix) => mix.id === selectedMixed.value[0].id);
        if (hasItem) {
          return;
        }
        const mix = selectedMixed.value.map((item) => ({ ...item, selected: false }));
        driveStore.currentFolder.mixedData = [...mix, ...driveStore.currentFolder.mixedData!];
      }
    }

    function moveMixed(folderId: string | null = null) {
      // bigLoader.value = 'Moving files...';

      const promises = selectedMixedIds.value.map((item) => resolveMoveAction(item, folderId));
      setSkeletons(folderId, selectedMixedIds.value.length);
      Promise.all(promises)
        .then(() => {
          syncData(folderId);
          NotificationService.notifySuccess();
        })
        .finally(() => {
          removeSkeletons(folderId, selectedMixedIds.value.length);
        });
      selectedMixedIds.value.forEach((sel) => clearCachedItem(sel.id));
      emit('close');
    }

    watch(() => route.path, () => {
      driveStore.isMixedSelector = false;
    });

    return {
      driveStore,
      bigLoader,
      isMixedSelected,
      showMultiSelection,
      isMoveToPopUp,
      selectedMixedIds,
      deleteMixed,
      moveMixed,
      openMoveToPopUp,
      openQuestionPopUp,
      isQuestionPopUp,
      selectedMixed,
    };
  },
});
